import { useMemo, useState } from 'react';

import {
  createColumnHelper,
  FilterFn,
  filterFns,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import moment from 'moment';
import { useNavigate, useParams } from 'react-router-dom';

import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useDeleteChronology } from '@/api/mutations/useDeleteChronology';
import { useGetAllChronologies } from '@/api/queries/useGetAllChronologies';
import { useGetMatterChronologies } from '@/api/queries/useGetMatterChronologies';
import { useGetSubset } from '@/api/queries/useGetSubset';
import { Chronology } from '@/api/types/backend-dto-types';
import { useUserContext } from '@/Contexts/User';
import formatDate from '@/helpers/formatDate';
import { Chip } from '@/new/components/chip';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import { FilterPopoverContent } from '@/new/components/filter-popover-content';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Label } from '@/new/components/shadcn/label';
import { Switch } from '@/new/components/shadcn/switch';
import { ScrollableTable } from '@/new/components/table/scrollable-table';
import { TableNullField } from '@/new/components/table/table-null-field';
import { CHIP_COLOURS } from '@/new/lib/chip-colours';
import { cn } from '@/new/lib/utils';

interface ChronologyGlobalFilter {
  query: string;
  hideSubsetChronologies: boolean;
}

const fallbackTableData: Chronology[] = [];
const columnHelper = createColumnHelper<Chronology>();

function ActionsCell({ chronology }: { chronology: Chronology }) {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const { user } = useUserContext();

  const { mutate: deleteChronology, isLoading: isDeleting } = useDeleteChronology({
    chronologyId: chronology.id,
    caseId: chronology.caseId,
  });

  const handleDeleteChronology = async () => {
    deleteChronology();
    trackCustomEvent(TrackingEventsV2.DeleteChronology, { chronologyId: chronology.id });
    setDeleteModalOpen(false);
  };

  const canDelete = user?.id === chronology.createdBy.id;

  const handleDeleteClick = () => {
    if (canDelete) {
      setDeleteModalOpen(true);
    }
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="destructive"
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteClick();
          }}
          className="-my-1"
          disabled={!canDelete}
        >
          Delete
        </Button>
      </div>

      <ConfirmationModal
        onOpenChange={setDeleteModalOpen}
        isOpen={deleteModalOpen}
        title="Delete chronology"
        onConfirm={handleDeleteChronology}
        isLoading={isDeleting}
      >
        <p>Are you sure you want to delete this chronology?</p>
      </ConfirmationModal>
    </>
  );
}

const columns = [
  columnHelper.accessor('title', {
    header: () => <span>Title</span>,
    cell: (info) => (
      <div className="flex items-center gap-2">
        {info.row.original.case && (
          <Chip className={cn(CHIP_COLOURS.subset, 'border')}>
            <Icon className="lucide--group" />
            <span className="line-clamp-1 max-w-42 overflow-ellipsis">{info.row.original.case.name}</span>
          </Chip>
        )}
        <span className="line-clamp-1 break-all">{info.getValue()}</span>
      </div>
    ),
  }),
  columnHelper.accessor('createdDate', {
    header: () => <span>Date Created</span>,
    cell: (info) => {
      const date = new Date(info.getValue());
      return <div className="truncate">{formatDate(date)}</div>;
    },
  }),
  columnHelper.accessor('createdBy', {
    header: () => <span>Created By</span>,
    cell: (info) => {
      const createdBy = info.getValue();
      return <span>{createdBy?.email || 'Unknown'}</span>;
    },
  }),
  columnHelper.accessor('editedBy', {
    header: () => <span>Last Updated By</span>,
    cell: (info) => {
      const editedBy = info.getValue();
      return editedBy ? <span>{editedBy.email}</span> : <TableNullField />;
    },
  }),
  columnHelper.accessor('editedDate', {
    header: () => <span>Last Updated</span>,
    cell: (info) => {
      const editedDate = info.getValue();
      const lastUpdatedDate = editedDate || info.row.original.createdDate;
      return <span>{moment(new Date(lastUpdatedDate)).fromNow()}</span>;
    },
  }),
  columnHelper.display({
    id: 'actions',
    header: () => <span>Actions</span>,
    cell: (info) => <ActionsCell chronology={info.row.original} />,
  }),
];

export function ChronologiesTable() {
  const [globalFilter, setGlobalFilter] = useState<ChronologyGlobalFilter>({
    query: '',
    hideSubsetChronologies: false,
  });
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) return null;

  const navigate = useNavigate();

  const clearAllFilters = () => {
    setGlobalFilter({ query: '', hideSubsetChronologies: false });
  };

  // Fetch subset chronologies
  const { data: subsetChronologies, isLoading: isLoadingSubsetChronologies } = useGetAllChronologies({ subsetId });

  // Fetch case data to check if this is the "All Documents" subset
  const { data: subset } = useGetSubset(matterId, subsetId);

  // Conditionally fetch matter chronologies only if this is the All Documents subset
  const { data: matterChronologies, isLoading: isLoadingMatterChronologies } = useGetMatterChronologies({
    matterId: subset?.isAllDocuments ? matterId : null,
  });

  // Merge and deduplicate chronologies
  const allChronologies = useMemo(() => {
    if (!subsetChronologies) return fallbackTableData;
    if (!subset?.isAllDocuments || !matterChronologies) return subsetChronologies;

    // Create a Set of chronology IDs from subset chronologies to avoid duplicates
    const subsetChronologyIds = new Set(subsetChronologies.map((c) => c.id));

    // Filter out matter chronologies that are already in subset chronologies
    const uniqueMatterChronologies = matterChronologies.filter((c) => !subsetChronologyIds.has(c.id));

    // Combine and sort with All Documents chronologies first, then by created date within each group
    return [...subsetChronologies, ...uniqueMatterChronologies].sort((a, b) => {
      const aIsFromCurrentSubset = a.caseId === subsetId;
      const bIsFromCurrentSubset = b.caseId === subsetId;

      // If one is from current subset and the other isn't, prioritize the current subset
      if (aIsFromCurrentSubset && !bIsFromCurrentSubset) return -1;
      if (!aIsFromCurrentSubset && bIsFromCurrentSubset) return 1;

      // Otherwise, sort by created date (most recent first)
      return new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();
    });
  }, [subsetChronologies, matterChronologies, subset?.isAllDocuments, subsetId]);

  // Custom global filter function
  const customGlobalFilter: FilterFn<Chronology> = (row, columnId, filterValue: ChronologyGlobalFilter) => {
    const { query, hideSubsetChronologies } = filterValue;

    // Filter out chronologies from other subsets if hidden
    if (hideSubsetChronologies && row.original.caseId !== subsetId) {
      return false;
    }

    // Apply text search filter
    if (query) {
      return filterFns.includesString(row, columnId, query, () => null);
    }

    return true;
  };

  const table = useReactTable({
    data: allChronologies,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: customGlobalFilter,
    initialState: {
      pagination: {
        pageSize: 15,
      },
    },
    state: {
      globalFilter,
    },
  });

  if (!subsetChronologies) return null;

  const isAllDocumentsSubset = subset?.isAllDocuments;

  return (
    <ScrollableTable
      table={table}
      plural="chronologies"
      globalFilter={globalFilter.query}
      setGlobalFilter={(value) =>
        setGlobalFilter((prev) => ({
          ...prev,
          query: typeof value === 'function' ? value(prev.query) : value,
        }))
      }
      filterPopover={
        isAllDocumentsSubset ? (
          <FilterPopoverContent clearAllFilters={clearAllFilters}>
            <div className="flex items-center gap-2">
              <Switch
                id="hide-subset-chronologies"
                checked={globalFilter.hideSubsetChronologies}
                onCheckedChange={(checked) => setGlobalFilter({ ...globalFilter, hideSubsetChronologies: checked })}
              />
              <Label htmlFor="hide-subset-chronologies" className="m-0">
                Hide chronologies from other subsets
              </Label>
            </div>
          </FilterPopoverContent>
        ) : undefined
      }
      clearAllFilters={isAllDocumentsSubset ? clearAllFilters : undefined}
      rowOnClick={(row) => {
        trackCustomEvent(TrackingEventsV2.ClickViewChronology, { chronologyId: row.original.id, subsetId });
        navigate(
          row.original.case
            ? `/matters/${matterId}/subsets/${row.original.caseId}/chronologies/${row.original.id}`
            : row.original.id,
        );
      }}
      loading={isLoadingSubsetChronologies || isLoadingMatterChronologies}
    />
  );
}
